// components/AgentPlaybookModal.tsx
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AutoGrowingTextarea from './AutoGrowingTextarea';
import SimpleLoader from './ui/SimpleLoader';
import { X } from '@/components/ui/lucide-memo';
import { useKernelStore } from '@/kernel/kernelStores';

interface AgentPlaybookModalProps {
  isOpen: boolean;
  onClose: () => void;
  organisationId: string | null;
  onSave: (newPlaybook: string) => void;
}

const AgentPlaybookModal: React.FC<AgentPlaybookModalProps> = ({
  isOpen,
  onClose,
  organisationId,
  onSave,
}) => {
  const organisationName = useKernelStore(
    state => (organisationId ? state.inventory.Organisation.registry[organisationId]?.name : null) ?? null,
  );
  const initialPlaybook = useKernelStore(
    state => (organisationId ? state.inventory.Organisation.registry[organisationId]?.agentPlaybook : null) ?? null,
  );
  const [playbook, setPlaybook] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing in this file, inherited by the merge gate (it judges whole files); changing it alters behaviour outside this merge
      setPlaybook(initialPlaybook || '');
    } else {
      // Reset state when modal is closed
      setPlaybook('');
      setError(null);
      setIsSaving(false);
    }
  }, [isOpen, initialPlaybook]);

  const stopPropagation = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const handlePlaybookChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setPlaybook(e.target.value);
  }, []);

  const handleSave = useCallback(async () => {
    if (!organisationId) return;
    setIsSaving(true);
    setError(null);
    try {
      await axios.post('/api/organisation/members', {
        action: 'updatePlaybook',
        organisationId: organisationId,
        agentPlaybook: playbook,
      });
      toast.success(`Agent playbook for "${organisationName}" saved.`);
      onSave(playbook);
      onClose();
    } catch (err: any) {
      console.error('Failed to save agent playbook:', err);
      const errorMessage = err.response?.data?.message || 'An error occurred while saving.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  }, [organisationId, organisationName, playbook, onSave, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[101] flex items-center justify-center bg-black/60 p-4">
      <div
        className="relative flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-[24px] border border-white/[0.06] bg-[#212121] text-white shadow-2xl"
        onClick={stopPropagation}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full text-[#B4B4B4] transition-colors hover:bg-white/[0.08] hover:text-white"
          aria-label="Close"
          disabled={isSaving}
        >
          <X size={17} />
        </button>

        <header className="border-b border-white/[0.055] px-5 py-4 pr-12">
          <h2 className="text-[18px] font-medium text-[#ECECEC]">Agent playbook</h2>
          <p className="mt-1 text-sm text-white/45">
            Set the shared guidance for {organisationName || 'this organisation'}.
          </p>
        </header>

        <div className="custom-scrollbar flex-grow overflow-y-auto p-5">
          <div>
            <label htmlFor="playbook-textarea" className="mb-1.5 block text-xs font-medium text-white/55">
              Instructions
            </label>
            <AutoGrowingTextarea
              id="playbook-textarea"
              value={playbook}
              onChange={handlePlaybookChange}
              minHeight={280}
              className="max-h-[58vh] w-full resize-y rounded-[16px] border border-[#2F2F2F] bg-transparent p-4 !text-[15px] leading-6 text-[#ECECEC] outline-none placeholder:text-[#8E8E8E] focus:border-white/20"
              placeholder={`Enter a concise guide for the agent on this organisation's goals, brand, and common tasks...`}
            />
            <p className="mt-1.5 text-xs text-white/35">
              The agent uses this context to align its decisions with the organisation&apos;s goals and working style.
            </p>
          </div>

          {error && <p className="mt-3 text-sm text-[#FF7369]">{error}</p>}
        </div>

        <footer className="flex shrink-0 justify-end gap-2 border-t border-white/[0.055] px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 items-center rounded-full px-4 !text-[15px] text-[#ECECEC] transition-colors hover:bg-white/[0.06] disabled:opacity-40"
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="inline-flex h-9 min-w-24 shrink-0 items-center justify-center whitespace-nowrap rounded-full bg-[#F0F0F0] px-4 !text-[15px] font-medium text-black transition-colors hover:bg-white disabled:cursor-not-allowed disabled:opacity-40"
            disabled={isSaving}
          >
            {isSaving ? <SimpleLoader size={16} /> : 'Save playbook'}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default React.memo(AgentPlaybookModal);
